// S42 Phase 1 — Trust hub page describing the label-grounded DDI reference.
// Positioned explicitly as Educational reference, NOT Clinical Decision
// Support (FDA SaMD line guard).

import { LegalPage } from "../../components/LegalPage";

export function DdiMethodologyPage() {
  return (
    <LegalPage
      title="Drug interaction reference — methodology"
      eyebrow="Trust center"
      description="How WeConsult Rx surfaces drug-drug interactions: FDA-approved labeling (DailyMed), RxNorm, structured Bedrock pull with verbatim-quote verification. Educational reference, not clinical decision support."
      path="/trust/ddi-methodology"
      effective="2026-05-31"
    >
      <p>
        WeConsult Rx surfaces drug-drug interaction (DDI) information as an{" "}
        <strong>educational reference</strong>. It is{" "}
        <strong>not clinical decision support</strong>, not medical advice, and
        is not a substitute for clinical judgment, pharmacist review, or the
        prescribing information itself. Clinicians must apply their own
        judgment to every prescribing decision.
      </p>

      <h2>Data sources</h2>
      <ul>
        <li>
          <strong>DailyMed</strong> (U.S. National Library of Medicine) &mdash;
          authoritative repository of FDA-approved structured product labeling
          (SPL). We ingest Section 7 (Drug Interactions, LOINC{" "}
          <code>34073-7</code>) from each label.
        </li>
        <li>
          <strong>RxNorm</strong> (NLM) &mdash; standardized drug nomenclature.
          We map every interacting drug name to a stable RxNorm Concept Unique
          Identifier (RxCUI) so pair lookups are exact.
        </li>
        <li>
          <strong>openFDA Drug Labels</strong> &mdash; supplemental metadata
          (SPL set IDs, effective dates).
        </li>
      </ul>

      <h2>Pull methodology</h2>
      <ol>
        <li>
          For each seed RxCUI in our long-term-care formulary, fetch the most
          recent SPL XML from DailyMed.
        </li>
        <li>
          Parse Section 7 (Drug Interactions). Preserve the verbatim text.
        </li>
        <li>
          Send the verbatim Section 7 text to an Anthropic Claude model via
          AWS Bedrock with a structured JSON schema. The model returns an
          array of interaction assertions: interacting drug name, an{" "}
          <em>exact_quote</em> from the source text, severity language (if any),
          mechanism (if stated), and recommendation (if stated).
        </li>
        <li>
          <strong>Verbatim verification</strong>: every{" "}
          <code>exact_quote</code> must appear verbatim in the source label
          (case-insensitive, whitespace-normalized). Assertions whose quote
          cannot be verified are <strong>discarded</strong> &mdash; never
          persisted, never displayed. The same verification runs again at
          query time.
        </li>
        <li>
          The interacting drug name is mapped to an RxCUI via local RxNorm
          lookup, falling back to RxNav approximateTerm. If no RxCUI can be
          resolved with certainty, the assertion is{" "}
          <strong>discarded</strong> &mdash; we never invent identifiers.
        </li>
        <li>
          Manufacturer-stated severity language ("Major", "Contraindicated",
          "Use with caution") is preserved verbatim. We additionally map it to
          a coarse class (contraindicated / major / moderate / minor / info)
          used only for sorting + badge color.
        </li>
      </ol>

      <h2>Coverage and limitations</h2>
      <ul>
        <li>
          <strong>Scope</strong>: only interactions documented in
          FDA-approved Section 7 of an SPL. Interactions known to clinicians
          but not enumerated in labeling are <strong>not</strong> in this
          dataset.
        </li>
        <li>
          <strong>Phase 1 seed</strong>: top ~150 long-term-care drug
          ingredients (CMS Part D top-prescribed list). Coverage is being
          expanded each ingest run.
        </li>
        <li>
          Many pairs are not addressed in either drug&apos;s label. The chart
          panel surfaces an explicit warning when this is the case (
          <em>&quot;N pairs aren&apos;t addressed in FDA-approved
          labeling&quot;</em>).
        </li>
        <li>
          Severity classifications combine manufacturer language with
          conservative keyword mapping. The verbatim label text is always shown
          alongside the classification so clinicians can apply their own
          judgment.
        </li>
        <li>
          We do <strong>not</strong> currently incorporate post-marketing
          literature, case reports, or pharmacokinetic prediction. These are
          on the roadmap for a future phase.
        </li>
      </ul>

      <h2>Update cadence</h2>
      <p>
        The full DailyMed corpus for our seed list is re-ingested monthly.
        Labels change infrequently; emergency label updates (FDA safety
        communications) are ingested out-of-band on a best-effort basis.
      </p>

      <h2>Positioning &mdash; educational reference, not CDS</h2>
      <p>
        WeConsult Rx is{" "}
        <strong>not a clinical decision support system (CDSS)</strong> for the
        purposes of FDA Software as a Medical Device (SaMD) classification.
        The DDI panel:
      </p>
      <ul>
        <li>Surfaces information from FDA-approved labeling for clinician review.</li>
        <li>
          Does not recommend, prioritize, or filter clinical actions. The
          clinician retains full discretion over prescribing decisions.
        </li>
        <li>
          Always provides the verbatim source quote, the SPL set ID, and a
          deep link to the source label on DailyMed, so clinicians can
          independently verify every assertion.
        </li>
      </ul>

      <h2>Contact</h2>
      <p>
        Questions or feedback about how an interaction is surfaced? Email{" "}
        <a href="mailto:clinical@weconsultrx.com">clinical@weconsultrx.com</a>.
        For security reports related to the methodology pipeline, see our{" "}
        <a href="/responsible-disclosure">responsible disclosure</a> page.
      </p>
    </LegalPage>
  );
}
